import {
  canonicalApplicationWebMcpToolRefs,
  parseApplicationWebMcpToolRef,
} from "@cogo/types-v2";
import { admitLibraryComposition } from "./admit-library-composition";
import {
  applicationNamespace,
  blockActionId,
  blockNamespace,
  exactResourceId,
  pageNamespace,
  uxInstanceNamespace,
} from "./exact-namespace";
import type {
  ApplicationBlueprint,
  ApplicationCapabilityRequirement,
  ApplicationRuntimeCapabilityProfile,
  ApplicationWebMcpManifest,
  ApplicationWebMcpToolManifestEntry,
  ResolvedPortBinding,
  ScopeManifestEntry,
  LibraryCompileResult,
  LibraryPort,
  LibraryPortBinding,
  LibraryPortEndpoint,
  UxPatternDefinition,
  UxPatternInstance,
} from "./types";

export function compileLibraryComposition(
  candidate: unknown,
  authorityContext: unknown,
): LibraryCompileResult {
  const admission = admitLibraryComposition(candidate, authorityContext);
  if (!admission.ok) return admission;

  const input = admission.input;
  const app = input.applicationBlueprint;
  const appNamespace = applicationNamespace(
    app.applicationBlueprintId,
    input.applicationInstanceId,
  );
  const definitions = new Map(
    input.uxPatterns.map((definition) => [
      `${definition.uxPatternId}@${definition.version}`,
      definition,
    ]),
  );

  const scopeManifest: ScopeManifestEntry[] = [
    {
      scopeKind: "application" as const,
      ownerRef: `${app.applicationBlueprintId}@${app.version}`,
      namespaceId: appNamespace,
    },
    ...app.uxInstances.map((instance) => ({
      scopeKind: "ux-instance" as const,
      ownerRef: instance.definitionRef,
      namespaceId: uxInstanceNamespace(appNamespace, instance.uxInstanceId),
    })),
  ];

  const states = [
    ...app.applicationStates.map((stateId) =>
      exactResourceId(appNamespace, "state", stateId),
    ),
    ...app.uxInstances.flatMap((instance) => {
      const definition = exactDefinition(definitions, instance.definitionRef);
      const namespaceId = uxInstanceNamespace(
        appNamespace,
        instance.uxInstanceId,
      );
      return definition.states.map((state) =>
        exactResourceId(namespaceId, "state", state.stateId),
      );
    }),
  ];

  const semanticNodes = app.uxInstances.flatMap((instance) => {
    const definition = exactDefinition(definitions, instance.definitionRef);
    const namespaceId = uxInstanceNamespace(
      appNamespace,
      instance.uxInstanceId,
    );
    return definition.semanticNodes.map((node) => ({
      nodeId: exactResourceId(namespaceId, "node", node.nodeId),
      semanticRole: node.semanticRole,
      regionRef: node.regionRef,
      content: structuredClone(node.content),
      visibleIn: node.visibleIn.map((stateId) =>
        exactResourceId(namespaceId, "state", stateId),
      ),
      ...(node.action ? { action: structuredClone(node.action) } : {}),
    }));
  });

  const resolvedBindings = app.bindings.map((binding) =>
    resolveBinding(binding, appNamespace, app.uxInstances),
  );
  const applicationStateIds = app.applicationStates.map((stateId) =>
    exactResourceId(appNamespace, "state", stateId),
  );
  const applicationEventIds = app.applicationEvents.map((eventId) =>
    exactResourceId(appNamespace, "event", eventId),
  );
  const applicationFlows = app.applicationFlows.map((flow) => ({
    flowId: exactResourceId(appNamespace, "flow", flow.flowId),
    eventId: exactResourceId(appNamespace, "event", flow.eventRef),
    capabilityRef: flow.capabilityRef,
  }));
  const datasources = app.datasourceRequirements.map((datasource) => ({
    datasourceId: exactResourceId(
      appNamespace,
      "datasource",
      datasource.datasourceId,
    ),
    capabilityRef: datasource.capabilityRef,
  }));
  const applicationPorts = app.applicationPorts.map((port) =>
    compilePort(port, appNamespace),
  );
  const uxStatecharts = app.uxInstances.map((instance) => {
    const definition = exactDefinition(definitions, instance.definitionRef);
    const namespaceId = uxInstanceNamespace(
      appNamespace,
      instance.uxInstanceId,
    );
    return {
      uxInstanceId: instance.uxInstanceId,
      namespaceId,
      definitionRef: instance.definitionRef,
      intent: definition.intent,
      actorContext: definition.actorContext,
      requiredCapabilities: [...definition.requiredCapabilities],
      preconditions: [...definition.preconditions],
      postconditions: [...definition.postconditions],
      states: definition.states.map((state) => ({
        stateId: exactResourceId(namespaceId, "state", state.stateId),
        statusIntent: state.statusIntent,
      })),
      eventIds: definition.ports
        .filter((port) => port.kind === "event")
        .map((port) => exactResourceId(namespaceId, "event", port.portId)),
      ports: definition.ports.map((port) => compilePort(port, namespaceId)),
      transitions: definition.transitions.map((transition) => ({
        transitionId: exactResourceId(
          namespaceId,
          "flow",
          transition.transitionId,
        ),
        fromStateId: exactResourceId(
          namespaceId,
          "state",
          transition.fromState,
        ),
        eventId: exactResourceId(
          namespaceId,
          "event",
          transition.eventRef,
        ),
        toStateId: exactResourceId(
          namespaceId,
          "state",
          transition.toState,
        ),
        effectActionIds: transition.effectRefs.map((effectRef) =>
          exactResourceId(namespaceId, "action", effectRef),
        ),
        emittedEventIds: transition.emitEventRefs.map((eventRef) =>
          exactResourceId(namespaceId, "event", eventRef),
        ),
      })),
    };
  });

  const ownedPages = app.journey.steps.flatMap((step) =>
    step.uxInstanceIds.map((uxInstanceId) => {
      const instance = exactInstance(app.uxInstances, uxInstanceId);
      const uxNamespaceId = uxInstanceNamespace(appNamespace, uxInstanceId);
      return {
        pageId: pageNamespace(uxNamespaceId, step.stepId),
        journeyStepId: exactResourceId(
          appNamespace,
          "journey-step",
          step.stepId,
        ),
        uxInstanceNamespaceId: uxNamespaceId,
        definition: exactDefinition(definitions, instance.definitionRef),
      };
    }),
  );
  const pages = ownedPages.map(
    ({ pageId, journeyStepId, uxInstanceNamespaceId }) => ({
      pageId,
      journeyStepId,
      uxInstanceNamespaceId,
    }),
  );
  const blocks = ownedPages.flatMap((page) =>
    page.definition.semanticNodes.map((node) => ({
      blockId: blockNamespace(page.pageId, node.nodeId),
      pageId: page.pageId,
      uxInstanceNamespaceId: page.uxInstanceNamespaceId,
      semanticNodeId: exactResourceId(
        page.uxInstanceNamespaceId,
        "node",
        node.nodeId,
      ),
    })),
  );
  const semanticActions = ownedPages.flatMap((page) =>
    page.definition.semanticNodes.flatMap((node) => {
      if (!node.action) return [];
      const blockId = blockNamespace(page.pageId, node.nodeId);
      return [
        {
          actionId: blockActionId(blockId, "semantic-action"),
          blockId,
          uxInstanceNamespaceId: page.uxInstanceNamespaceId,
          declaration: structuredClone(node.action),
        },
      ];
    }),
  );
  const results = [
    ...app.applicationPorts
      .filter((port) => port.direction === "out")
      .map((port) => ({
        resultId: exactResourceId(appNamespace, "result", port.portId),
        portId: exactResourceId(appNamespace, "port", port.portId),
        ownerScopeId: appNamespace,
      })),
    ...app.uxInstances.flatMap((instance) => {
      const namespaceId = uxInstanceNamespace(
        appNamespace,
        instance.uxInstanceId,
      );
      const definition = exactDefinition(definitions, instance.definitionRef);
      return definition.ports
        .filter((port) => port.direction === "out")
        .map((port) => ({
          resultId: exactResourceId(namespaceId, "result", port.portId),
          portId: exactResourceId(namespaceId, "port", port.portId),
          ownerScopeId: namespaceId,
        }));
    }),
  ];
  const journey = {
    entryStepId: exactResourceId(
      appNamespace,
      "journey-step",
      app.journey.entryStepId,
    ),
    terminalStepIds: app.journey.terminalStepIds.map((stepId) =>
      exactResourceId(appNamespace, "journey-step", stepId),
    ),
    steps: app.journey.steps.map((step) => ({
      stepId: exactResourceId(appNamespace, "journey-step", step.stepId),
      uxInstanceNamespaceIds: step.uxInstanceIds.map((uxInstanceId) =>
        uxInstanceNamespace(appNamespace, uxInstanceId),
      ),
      pageIds: step.uxInstanceIds.map((uxInstanceId) =>
        pageNamespace(
          uxInstanceNamespace(appNamespace, uxInstanceId),
          step.stepId,
        ),
      ),
      transitions: step.transitions.map((transition) => ({
        eventId: exactResourceId(
          appNamespace,
          "event",
          transition.eventRef,
        ),
        toStepId: exactResourceId(
          appNamespace,
          "journey-step",
          transition.toStepId,
        ),
        path: transition.path,
      })),
    })),
  };
  const actionIds = uniqueInOrder([
    ...uxStatecharts.flatMap((statechart) =>
      statechart.transitions.flatMap(
        (transition) => transition.effectActionIds,
      ),
    ),
    ...semanticActions.map((action) => action.actionId),
  ]);
  const runtimeCapabilities = compileRuntimeCapabilityProfile(
    app,
    input.capabilityRequirement,
  );
  const applicationWebMcpManifest = compileApplicationWebMcpManifest(
    app,
    appNamespace,
    input.applicationInstanceId,
    runtimeCapabilities,
  );
  const uxPatternRefs = [
    ...new Set(app.uxInstances.map((instance) => instance.definitionRef)),
  ].sort();
  const sourceRefs = [
    ...new Set([
      ...app.lineage.sourceRefs,
      ...uxPatternRefs.flatMap(
        (definitionRef) =>
          exactDefinition(definitions, definitionRef).lineage.sourceRefs,
      ),
    ]),
  ].sort();

  return {
    ok: true,
    artifact: {
      schemaVersion: "cogo.library-composition-artifact.v1",
      applicationBlueprintRef: `${app.applicationBlueprintId}@${app.version}`,
      applicationInstanceId: input.applicationInstanceId,
      scopeManifest,
      resolvedBindings,
      resolvedApplicationFlowIds: applicationFlows.map((flow) => flow.flowId),
      runtime: {
        userGoal: app.userGoal,
        actor: app.actor,
        capabilities: [...app.capabilities],
        applicationStateIds,
        applicationEventIds,
        applicationFlows,
        datasources,
        applicationPorts,
        uxStatecharts,
        journey,
        portBindings: structuredClone(resolvedBindings),
        pages,
        blocks,
        semanticActions,
        results,
        pageIds: pages.map((page) => page.pageId),
        blockIds: blocks.map((block) => block.blockId),
        actionIds,
        resultIds: results.map((result) => result.resultId),
      },
      runtimeCapabilities,
      ...(applicationWebMcpManifest ? { applicationWebMcpManifest } : {}),
      plan: {
        schemaVersion: "cogo.application-composition-plan.v1",
        planId: appNamespace,
        applicationCoreRef: app.applicationCoreRef,
        target: app.target,
        locale: app.locale,
        recipeRef: app.recipeRef,
        states,
        semanticNodes,
      },
      provenance: {
        applicationCoreRef: app.applicationCoreRef,
        applicationBlueprintRef: `${app.applicationBlueprintId}@${app.version}`,
        uxPatternRefs,
        sourceRefs,
        legacyRefs: [],
      },
      generationTrace: {
        mode: "deterministic-library-composition",
        legacyStudioArtifactUsed: false,
        genericFallbackUsed: false,
        rawPresentationAuthoredByModel: false,
      },
    },
  };
}

/**
 * Compile the request-bound capability profile. Admission already proved that
 * the Blueprint declaration, the request mode, and every tool ref agree, so this
 * only canonicalizes what was admitted.
 */
function compileRuntimeCapabilityProfile(
  app: ApplicationBlueprint,
  requirement: ApplicationCapabilityRequirement,
): ApplicationRuntimeCapabilityProfile {
  const webMcp = app.runtimeCapabilities.webMcp;
  return {
    schemaVersion: "cogo.application-runtime-capabilities.v1",
    webMcp: {
      mode: webMcp.mode,
      toolRefs: canonicalApplicationWebMcpToolRefs(webMcp.toolRefs),
      approvalPolicyRef: webMcp.approvalPolicyRef,
      requirementEvidenceRefs: [
        ...new Set(requirement.requirementEvidenceRefs),
      ].sort(),
    },
  };
}

/**
 * Compile the application WebMCP manifest. `disabled` compiles no manifest at
 * all — the property is omitted from the artifact, never emitted empty.
 */
function compileApplicationWebMcpManifest(
  app: ApplicationBlueprint,
  appNamespace: string,
  applicationInstanceId: string,
  profile: ApplicationRuntimeCapabilityProfile,
): ApplicationWebMcpManifest | undefined {
  if (profile.webMcp.mode === "disabled") return undefined;
  const toolNamespace = `cogo.application.${app.applicationBlueprintId}.${applicationInstanceId}`;
  const tools = profile.webMcp.toolRefs.map((toolRef) => {
    const parsed = parseApplicationWebMcpToolRef(toolRef);
    if (!parsed) {
      throw new Error(`Admission accepted a malformed tool ref ${toolRef}.`);
    }
    return parsed.kind === "datasource"
      ? datasourceToolEntry(
          app,
          appNamespace,
          toolNamespace,
          toolRef,
          parsed.localId,
        )
      : flowToolEntry(app, appNamespace, toolNamespace, toolRef, parsed.localId);
  });
  return {
    schemaVersion: "cogo.application-webmcp-manifest.v1",
    approvalPolicyRef: profile.webMcp.approvalPolicyRef,
    tools,
  };
}

function datasourceToolEntry(
  app: ApplicationBlueprint,
  appNamespace: string,
  toolNamespace: string,
  toolRef: string,
  localId: string,
): ApplicationWebMcpToolManifestEntry {
  const requirement = app.datasourceRequirements.find(
    (candidate) => candidate.datasourceId === localId,
  );
  if (!requirement) {
    throw new Error(`Admission accepted an unresolved datasource ${localId}.`);
  }
  return {
    sourceRef: toolRef as `datasource:${string}`,
    resourceId: exactResourceId(appNamespace, "datasource", localId),
    kind: "datasource",
    name: `${toolNamespace}.datasource.${localId}`,
    description: `Read the admitted application datasource ${localId} for capability ${requirement.capabilityRef}.`,
    inputSchema: closedInputSchema(),
    metadata: {
      readOnly: true,
      confirmation: "never",
      executionAuthority: "runtime",
      retryPolicy: "safe",
      tags: ["application-webmcp", "datasource"],
    },
  };
}

function flowToolEntry(
  app: ApplicationBlueprint,
  appNamespace: string,
  toolNamespace: string,
  toolRef: string,
  localId: string,
): ApplicationWebMcpToolManifestEntry {
  const flow = app.applicationFlows.find(
    (candidate) => candidate.flowId === localId,
  );
  if (!flow) {
    throw new Error(`Admission accepted an unresolved application flow ${localId}.`);
  }
  return {
    sourceRef: toolRef as `flow:${string}`,
    resourceId: exactResourceId(appNamespace, "flow", localId),
    kind: "flow",
    name: `${toolNamespace}.flow.${localId}`,
    description: `Execute the admitted application flow ${localId} for capability ${flow.capabilityRef}.`,
    inputSchema: closedInputSchema(),
    metadata: {
      readOnly: false,
      confirmation: "when-mutating",
      executionAuthority: "runtime",
      retryPolicy: "never",
      tags: ["application-webmcp", "flow", "confirmation.required"],
    },
  };
}

/**
 * A zero-base Blueprint declares no tool parameter authority, so every compiled
 * tool exposes a closed, empty input schema instead of an invented one.
 */
function closedInputSchema(): ApplicationWebMcpToolManifestEntry["inputSchema"] {
  return { type: "object", properties: {}, additionalProperties: false };
}

function exactInstance(
  instances: UxPatternInstance[],
  uxInstanceId: string,
): UxPatternInstance {
  const instance = instances.find(
    (candidate) => candidate.uxInstanceId === uxInstanceId,
  );
  if (!instance) {
    throw new Error(`Admission accepted missing UX instance ${uxInstanceId}.`);
  }
  return instance;
}

function compilePort(port: LibraryPort, namespaceId: string) {
  return {
    portId: exactResourceId(namespaceId, "port", port.portId),
    kind: port.kind,
    direction: port.direction,
    valueType: port.valueType,
    required: port.required,
    ...(port.direction === "out"
      ? {
          resultId: exactResourceId(namespaceId, "result", port.portId),
        }
      : {}),
  };
}

function exactDefinition(
  definitions: Map<string, UxPatternDefinition>,
  definitionRef: string,
): UxPatternDefinition {
  const definition = definitions.get(definitionRef);
  if (!definition) {
    throw new Error(`Admission accepted missing definition ${definitionRef}.`);
  }
  return definition;
}

function resolveBinding(
  binding: LibraryPortBinding,
  appNamespace: string,
  instances: UxPatternInstance[],
): ResolvedPortBinding {
  return {
    bindingId: binding.bindingId,
    sourcePortId: resolveEndpoint(binding.source, appNamespace, instances),
    targetPortId: resolveEndpoint(binding.target, appNamespace, instances),
  };
}

function resolveEndpoint(
  endpoint: LibraryPortEndpoint,
  appNamespace: string,
  instances: UxPatternInstance[],
): string {
  if (endpoint.scope === "application") {
    return exactResourceId(appNamespace, "port", endpoint.portId);
  }
  const instance = instances.find(
    (candidate) => candidate.uxInstanceId === endpoint.uxInstanceId,
  );
  if (!instance || !endpoint.uxInstanceId) {
    throw new Error(
      "Admission accepted a missing or partial UX instance match.",
    );
  }
  return exactResourceId(
    uxInstanceNamespace(appNamespace, instance.uxInstanceId),
    "port",
    endpoint.portId,
  );
}

function uniqueInOrder(values: string[]): string[] {
  return [...new Set(values)];
}
